import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { battlePersonApi } from '../api/battlePerson';
import { battlesApi } from '../api/battles';
import ParticipantCard from '../components/battles/ParticipantCard';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbSelect from '../components/controls/HrnbSelect';
import HrnbSearchFilter from '../components/controls/HrnbSearchFilter';
import HrnbContainer from '../components/controls/HrnbContainer';
import { BackIcon } from '../icons';
import { UserContext } from '../context/UserContext';
import '../css/pages/BattleParticipantsPage.css';

const ROLE_OPTIONS = [
    { value: '', label: 'Все роли' },
    { value: 'ORGANIZER', label: 'Организатор' },
    { value: 'PARTICIPANT', label: 'Участник' },
    { value: 'JUDGE', label: 'Жюри' },
];

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'PENDING', label: 'Ожидает' },
    { value: 'APPROVED', label: 'Одобрен' },
    { value: 'REJECTED', label: 'Отклонён' },
    { value: 'WITHDRAWN', label: 'Отказался' },
];

export default function BattleParticipantsPage() {
    const { battleId } = useParams();
    const navigate = useNavigate();
    
    const { currentUser } = useContext(UserContext);

    const [participants, setParticipants] = useState([]);
    const [battle, setBattle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Проверка: является ли текущий пользователь организатором этого баттла
    const isUserOrganizer = useMemo(() => {
        if (!currentUser) return false;

        // Извлекаем publicProfileId текущего пользователя (profiles[0].id или publicProfile.id)
        const userProfileId = currentUser.publicProfile?.id
            || (Array.isArray(currentUser.profiles) && currentUser.profiles[0]?.id)
            || null;

        // Проверка через battle.organizers (BattlePersonResponse[])
        if (battle && battle.organizers && Array.isArray(battle.organizers)) {
            const fromOrganizers = battle.organizers.some((o) => {
                if (o.userId && o.userId === currentUser.id) return true;
                if (o.publicProfileId && o.publicProfileId === currentUser.id) return true;
                if (o.publicProfileId && userProfileId && o.publicProfileId === userProfileId) return true;
                if (o.nickname && currentUser.nickname && o.nickname === currentUser.nickname) return true;
                if (o.name && currentUser.name && o.name === currentUser.name) return true;
                return false;
            });
            if (fromOrganizers) return true;
        }

        // Fallback: проверка через загруженных participants
        if (participants && participants.length > 0) {
            const fromParticipants = participants.some((p) => {
                if (p.role !== 'ORGANIZER') return false;
                if (p.userId && p.userId === currentUser.id) return true;
                if (p.publicProfileId && p.publicProfileId === currentUser.id) return true;
                if (p.publicProfileId && userProfileId && p.publicProfileId === userProfileId) return true;
                if (p.nickname && currentUser.nickname && p.nickname === currentUser.nickname) return true;
                if (p.name && currentUser.name && p.name === currentUser.name) return true;
                return false;
            });
            if (fromParticipants) return true;
        }

        return false;
    }, [currentUser, battle, participants]);

    const fetchBattle = useCallback(async () => {
        try {
            const data = await battlesApi.get(battleId);
            setBattle(data);
        } catch (err) {
            console.error('[BattleParticipantsPage] Failed to fetch battle:', err.message);
        }
    }, [battleId]);

    const fetchParticipants = useCallback(async () => {
        try {
            setLoading(true);
            // NOTE: battleId, role, status — расширения API, не документированные в reference
            const params = { battleId };
            if (filterRole) params.role = filterRole;
            if (filterStatus) params.status = filterStatus;

            const data = await battlePersonApi.list(params);
            const participantsData = data?.content || [];
            setParticipants(participantsData);
        } catch (err) {
            console.error('[BattleParticipantsPage] Failed to fetch participants:', err.message);
            setError('Не удалось загрузить участников');
        } finally {
            setLoading(false);
        }
    }, [battleId, filterRole, filterStatus]);

    useEffect(() => {
        fetchBattle();
        fetchParticipants();
    }, [fetchBattle, fetchParticipants]);

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (query) {
            performSearch(query);
        } else {
            fetchParticipants();
        }
    };

    const performSearch = async (query) => {
        try {
            setLoading(true);
            // NOTE: search — расширение API, не документированное в reference
            const data = await battlePersonApi.list({
                battleId,
                search: query
            });
            const participantsData = data?.content || [];
            setParticipants(participantsData);
        } catch (err) {
            console.error('[BattleParticipantsPage] Search failed:', err.message);
            setError('Не удалось выполнить поиск');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (participantId) => {
        if (!window.confirm('Подтвердите одобрение участника?')) {
            return;
        }
        try {
            await battlePersonApi.approve(participantId);
            await fetchParticipants();
            await fetchBattle();
        } catch (err) {
            console.error('[BattleParticipantsPage] Failed to approve:', err.message);
            setError('Не удалось одобрить участника');
        }
    };

    const handleReject = async (participantId) => {
        if (!window.confirm('Подтвердите отклонение участника?')) {
            return;
        }
        try {
            await battlePersonApi.reject(participantId);
            await fetchParticipants();
            await fetchBattle();
        } catch (err) {
            console.error('[BattleParticipantsPage] Failed to reject:', err.message);
            setError('Не удалось отклонить участника');
        }
    };

    const handleWithdraw = async (participantId) => {
        if (!window.confirm('Подтвердите отзыв заявки?')) {
            return;
        }
        try {
            await battlePersonApi.withdraw(participantId);
            await fetchParticipants();
            await fetchBattle();
        } catch (err) {
            console.error('[BattleParticipantsPage] Failed to withdraw:', err.message);
            setError('Не удалось отозвать заявку');
        }
    };

    const filteredParticipants = participants.filter(p => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const nickname = (p.nickname || '').toLowerCase();
        const name = (p.name || '').toLowerCase();
        const city = (p.city || '').toLowerCase();
        return nickname.includes(query) || name.includes(query) || city.includes(query);
    });

    return (
        <HrnbContainer className="battle-participants-page">
            <div className="battle-participants-page-header">
                <HrnbButton variant="outline" onClick={() => navigate(`/battles/${battleId}`)} icon={<BackIcon />}>
                    Назад к баттлу
                </HrnbButton>
                <div>
                    <h1 className="battle-participants-page-title">
                        Участники баттла
                    </h1>
                    {battle && (
                        <p className="battle-participants-page-subtitle">
                            {battle.title}
                        </p>
                    )}
                </div>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="battle-participants-page-filters">
                <HrnbSearchFilter
                    search={search}
                    onSearchChange={setSearch}
                    onSearchSubmit={handleSearch}
                    placeholder="Поиск по имени или городу..."
                    debounceMs={300}
                />

                <div className="filters-row">
                    <div className="filter-group">
                        <label className="filter-label">Роль:</label>
                        <HrnbSelect
                            value={filterRole}
                            onChange={setFilterRole}
                            options={ROLE_OPTIONS}
                            className="filter-select"
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Статус:</label>
                        <HrnbSelect
                            value={filterStatus}
                            onChange={setFilterStatus}
                            options={STATUS_OPTIONS}
                            className="filter-select"
                        />
                    </div>
                </div>
            </div>

            <div className="battle-participants-page-content">
                {loading ? (
                    <div className="battle-participants-page-loading">
                        <HrnbLoader />
                    </div>
                ) : filteredParticipants.length > 0 ? (
                    <>
                        <div className="participants-count">
                            Найдено: <strong>{filteredParticipants.length}</strong>
                        </div>
                        <div className="participants-grid">
                            {filteredParticipants.map((participant) => (
                                <ParticipantCard
                                    key={participant.id}
                                    participant={participant}
                                    showActions={participant.applicationStatus === 'PENDING' && isUserOrganizer}
                                    onApprove={() => handleApprove(participant.id)}
                                    onReject={() => handleReject(participant.id)}
                                    onWithdraw={() => handleWithdraw(participant.id)}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="battle-participants-page-empty">
                        <p>Участников не найдено</p>
                    </div>
                )}
            </div>
        </HrnbContainer>
    );
}
